(() => {
  'use strict';
  if (customElements.get('occhio-recent-selection')) return;
  customElements.define('occhio-recent-selection', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.grid = this.querySelector('[data-recent-grid]');
      this.cards = [...this.grid.querySelectorAll('[data-recent-card]')];
      this.querySelectorAll('template[data-recent-deferred]').forEach(template => {
        this.cards.push(template.content.querySelector('[data-recent-card]'));
        template.remove();
      });
      this.fields = Object.fromEntries(['kind','brand','shape','material','budget','order'].map(key => [key, this.querySelector(`[data-recent-${key}]`)]));
      this.more = this.querySelector('[data-recent-more]');
      this.chips = this.querySelector('[data-recent-chips]');
      this.reset = this.querySelector('[data-recent-reset]');
      this.limit = 24;
      for (const key of ['brand','shape','material']) {
        const values = this.cards.flatMap(card => key === 'material' ? this.materials(card) : [card.dataset[key]]);
        [...new Set(values.filter(Boolean))].sort((a,b) => a.localeCompare(b)).forEach(value => {
          const option = document.createElement('option');
          option.value = value; option.textContent = value;
          this.fields[key].append(option);
        });
      }
      this.abort = new AbortController();
      const opts = {signal:this.abort.signal};
      const panel = this.querySelector('[data-recent-filter-panel]');
      const mobile = matchMedia('(max-width:749px)');
      panel.open = !mobile.matches;
      mobile.addEventListener('change', event => {if (!event.matches) panel.open = true;}, opts);
      Object.values(this.fields).forEach(field => {
        field.disabled = false;
        field.addEventListener(field.type === 'number' ? 'input' : 'change', () => {this.limit = 24; this.paint(true);}, opts);
      });
      this.reset.addEventListener('click', () => {
        for (const [key,field] of Object.entries(this.fields)) field.value = key === 'order' ? 'curated' : '';
        this.limit = 24; this.paint(true); this.fields.kind.focus();
      }, opts);
      this.more.addEventListener('click', () => {
        const previous = this.limit;
        this.limit += 24; this.paint();
        const next = this.grid.children[previous]?.querySelector('a');
        if (next) next.focus({preventScroll:true});
      }, opts);
      this.chips.addEventListener('click', event => {
        const button = event.target.closest('[data-remove-filter]');
        if (!button || !this.fields[button.dataset.removeFilter]) return;
        const field = this.fields[button.dataset.removeFilter];
        field.value = ''; this.limit = 24; this.paint(true); field.focus();
      }, opts);
      this.restore();
      window.addEventListener('popstate', () => {this.restore(); this.paint();}, opts);
      this.paint();
    }
    disconnectedCallback() { this.abort?.abort(); this.ready = false; }
    materials(card) { return (card.dataset.material || '').split(',').map(value => value.trim()).filter(Boolean); }
    restore() {
      const params = new URL(location.href).searchParams;
      for (const [key,field] of Object.entries(this.fields)) {
        const value = params.get(`occhio_${key}`) || (key === 'order' ? 'curated' : '');
        if (key === 'budget') field.value = /^\d+(?:\.\d{1,2})?$/.test(value) && Number(value) > 0 ? value : '';
        else field.value = [...field.options].some(option => option.value === value) ? value : (key === 'order' ? 'curated' : '');
      }
      this.limit = 24;
    }
    curatedOrder(items) {
      const remaining = [...items], firstRows = [], counts = new Map(), models = new Set();
      const modelKey = card => `${card.dataset.brand}|${card.dataset.model}`;
      while (remaining.length && firstRows.length < 8) {
        const distinct = remaining.filter(card => !models.has(modelKey(card)));
        const candidates = distinct.length ? distinct : remaining;
        const minimum = Math.min(...candidates.map(card => counts.get(card.dataset.brand) || 0));
        const selected = candidates.find(card => (counts.get(card.dataset.brand) || 0) === minimum);
        firstRows.push(selected); models.add(modelKey(selected));
        counts.set(selected.dataset.brand, (counts.get(selected.dataset.brand) || 0) + 1);
        remaining.splice(remaining.indexOf(selected), 1);
      }
      return [...firstRows,...remaining];
    }
    paint(updateURL = false) {
      const values = Object.fromEntries(Object.entries(this.fields).map(([key,field]) => [key,field.value]));
      const validBudget = this.fields.budget.validity.valid;
      this.fields.budget.setAttribute('aria-invalid', String(!validBudget));
      this.querySelector('[data-recent-budget-error]').hidden = validBudget;
      const budget = validBudget && values.budget ? Number(values.budget) * 100 : Infinity;
      let matching = this.cards.filter(card => ['kind','brand','shape'].every(key => !values[key] || card.dataset[key] === values[key]) && (!values.material || this.materials(card).includes(values.material)) && Number(card.dataset.price) <= budget);
      matching.sort((a,b) => values.order === 'low' ? Number(a.dataset.price) - Number(b.dataset.price) : values.order === 'high' ? Number(b.dataset.price) - Number(a.dataset.price) : Number(b.dataset.created) - Number(a.dataset.created));
      if (values.order === 'curated') matching = this.curatedOrder(matching);
      this.grid.replaceChildren(...matching.slice(0,this.limit));
      this.querySelector('[data-recent-count]').textContent = this.dataset.countTemplate.replace('__COUNT__', String(matching.length));
      this.querySelector('[data-recent-empty]').hidden = matching.length !== 0;
      this.more.hidden = matching.length <= this.limit;
      this.querySelector('[data-recent-shown]').textContent = this.dataset.shownTemplate.replace('__SHOWN__', String(Math.min(this.limit,matching.length))).replace('__TOTAL__', String(matching.length));
      this.chips.replaceChildren();
      for (const [key,field] of Object.entries(this.fields)) {
        if (key === 'order' || !field.value || (key === 'budget' && !validBudget)) continue;
        const text = key === 'budget' ? `${field.value} ${this.dataset.currency}` : field.selectedOptions[0].textContent;
        const button = document.createElement('button'); button.type = 'button'; button.dataset.removeFilter = key;
        const label = `${field.dataset.label}: ${text}`;
        button.textContent = `${label} ×`; button.setAttribute('aria-label', this.dataset.removeLabel.replace('__FILTER__',label));
        this.chips.append(button);
      }
      this.reset.hidden = this.chips.children.length === 0;
      this.querySelector('[data-recent-active]').hidden = this.chips.children.length === 0;
      if (updateURL) {
        const url = new URL(location.href);
        for (const [key,value] of Object.entries(values)) {
          if (value && !(key === 'order' && value === 'curated') && !(key === 'budget' && !validBudget)) url.searchParams.set(`occhio_${key}`,value);
          else url.searchParams.delete(`occhio_${key}`);
        }
        try {history.replaceState(history.state,'',url.href);} catch (_) {}
      }
      document.dispatchEvent(new CustomEvent('occhio:catalog-rendered'));
    }
  });
})();
